// PJC v10 — barre d'achat fixe : visible dès que le bouton principal n'est plus à l'écran (au-dessus ou en
// dessous, avec un seuil de défilement), pilotée par window.PJC_PURCHASE (assets/pjc-buy.js),
// inerte et masquée aux technologies d'assistance quand elle est cachée, masquée par les dialogues,
// le tiroir panier, le clavier mobile et le pied de page. Plus de succès affiché avant la réponse serveur.
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

const MOBILE_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// Part de la hauteur d'écran à défiler avant d'afficher la barre quand le bouton est encore plus bas.
const SHOW_AFTER_SCROLL_RATIO = 0.4;

const OVERLAY_SELECTOR = 'dialog[open], theme-drawer[open]';
const TEXT_ENTRY_SELECTOR =
  'input:not([type="radio"], [type="checkbox"], [type="button"], [type="submit"], [type="range"], [type="hidden"]), textarea, select, [contenteditable=""], [contenteditable="true"]';

/**
 * @typedef {object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - La barre fixe.
 * @property {HTMLButtonElement} addToCartButton - Le bouton de la barre.
 * @property {HTMLElement} [message] - Ligne de message (erreur ou consigne).
 * @property {HTMLElement} [price] - Conteneur du prix.
 * @property {HTMLElement} [priceFrom] - Mention « À partir de ».
 * @property {HTMLElement} [priceValue] - Prix.
 * @property {HTMLElement} [priceCompare] - Prix barré.
 * @property {HTMLElement} [compareValue] - Montant du prix barré.
 * @property {HTMLElement} [label] - Libellé du bouton.
 * @property {HTMLImageElement} [productImage] - Vignette.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];

  #abort = new AbortController();

  /** @type {IntersectionObserver | null} */
  #blockObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #overlayObserver = null;

  /** @type {Element | null} */
  #block = null;

  /** @type {MediaQueryList | null} */
  #mobile = null;

  #footerVisible = false;
  #overlayOpen = false;
  #textEntryFocused = false;
  #shown = false;
  #frame = 0;

  /** @type {number | undefined} */
  #focusTimer;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abort;
    this.#mobile = window.matchMedia(MOBILE_QUERY);
    this.#mobile.addEventListener('change', this.#evaluate, { signal });

    this.#observeBlock();
    this.#observeFooter();
    this.#observeOverlays();
    this.#observeHeight();

    // Capture : dès 990 px le thème fait défiler .page-wrapper (pas la fenêtre) ; les scrolls d'éléments ne remontent pas.
    window.addEventListener('scroll', this.#onScroll, { passive: true, capture: true, signal });
    window.addEventListener('resize', this.#onScroll, { passive: true, signal });
    document.addEventListener('focusin', this.#onFocusIn, { signal });
    document.addEventListener('focusout', this.#onFocusOut, { signal });
    document.addEventListener('pjc:purchase-change', this.#onPurchase, { signal });
    document.addEventListener('pjc:buy-state', this.#onBuyState, { signal });
    this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });

    if (window.PJC_PURCHASE) this.#render(window.PJC_PURCHASE);
    this.#evaluate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    this.#blockObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#overlayObserver?.disconnect();
    window.clearTimeout(this.#frame);
    window.clearTimeout(this.#focusTimer);

    const root = document.documentElement;
    root.style.removeProperty('--pjc-bottom-bar-offset');
    root.style.removeProperty('--pjc-sticky-h');
  }

  /* --- Visibilité ------------------------------------------------------------------------------- */

  #observeBlock() {
    this.#block = this.closest('.shopify-section')?.querySelector('.buy-buttons-block') ?? null;
    if (!this.#block) return;

    // L'observateur ne sert qu'à relancer l'évaluation : la décision se fait sur la position réelle.
    this.#blockObserver = new IntersectionObserver(() => this.#evaluate());
    this.#blockObserver.observe(this.#block);
  }

  #observeFooter() {
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Le pied de page doit entrer d'au moins 80 px avant que la barre ne s'efface.
    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        this.#footerVisible = entry.isIntersecting;
        this.#evaluate();
      },
      { rootMargin: '0px 0px -80px 0px' }
    );
    this.#footerObserver.observe(footer);
  }

  #observeOverlays() {
    const check = () => {
      const open = Boolean(document.querySelector(OVERLAY_SELECTOR));
      if (open === this.#overlayOpen) return;
      this.#overlayOpen = open;
      this.#evaluate();
    };

    this.#overlayObserver = new MutationObserver(check);
    this.#overlayObserver.observe(document.documentElement, {
      attributes: true,
      subtree: true,
      attributeFilter: ['open'],
    });
    check();
  }

  #observeHeight() {
    this.#resizeObserver = new ResizeObserver(() => this.#publishHeight());
    this.#resizeObserver.observe(this.refs.stickyBar);
    this.#publishHeight();
  }

  /** Publie la hauteur de la barre pour le décalage du contenu, du toast et des boutons flottants. */
  #publishHeight() {
    const height = Math.ceil(this.refs.stickyBar.getBoundingClientRect().height);
    const root = document.documentElement;
    root.style.setProperty('--pjc-sticky-h', `${height}px`);
    root.style.setProperty('--pjc-bottom-bar-offset', this.#shown ? `${height}px` : '0px');
  }

  // Évaluation regroupée toutes les 50 ms au plus pendant le défilement.
  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = window.setTimeout(() => {
      this.#frame = 0;
      this.#evaluate();
    }, 50);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || this.contains(target) || !target.matches(TEXT_ENTRY_SELECTOR)) return;
    window.clearTimeout(this.#focusTimer);
    this.#textEntryFocused = true;
    this.#evaluate();
  };

  #onFocusOut = () => {
    window.clearTimeout(this.#focusTimer);
    // Délai : tabuler d'un champ à l'autre ne doit pas faire clignoter la barre.
    this.#focusTimer = window.setTimeout(() => {
      this.#textEntryFocused = Boolean(document.activeElement?.matches(TEXT_ENTRY_SELECTOR));
      this.#evaluate();
    }, 120);
  };

  #evaluate = () => {
    const mobile = Boolean(this.#mobile?.matches);
    let wanted = false;

    if (this.#block && !this.#footerVisible && !this.#overlayOpen && !(mobile && this.#textEntryFocused)) {
      // Référence = le bouton principal (repli : le bloc d'achat s'il n'a pas de mise en page, ex. mode pack).
      const main = this.#block.querySelector('[data-pjc-buy-button="main"]');
      let rect = main?.getBoundingClientRect();
      if (!rect || rect.height === 0) rect = this.#block.getBoundingClientRect();

      // Le bouton compte comme visible seulement au-dessus de la zone que la barre occuperait : même critère
      // barre affichée ou masquée, donc pas de va-et-vient quand il affleure en bas d'écran.
      const reserve = Math.ceil(this.refs.stickyBar.getBoundingClientRect().height) || 72;
      const above = rect.bottom <= 0;
      const inView = !above && rect.top < window.innerHeight - reserve;
      // Bouton encore plus bas : seulement après un début de défilement (jamais au premier écran).
      const scrolled = Math.max(window.scrollY, document.querySelector('.page-wrapper')?.scrollTop ?? 0);
      wanted = !inView && (above || scrolled >= window.innerHeight * SHOW_AFTER_SCROLL_RATIO);
    }

    this.#setShown(wanted);
  };

  /** @param {boolean} shown */
  #setShown(shown) {
    if (shown === this.#shown) return;
    this.#shown = shown;

    const { stickyBar } = this.refs;
    stickyBar.dataset.stuck = shown ? 'true' : 'false';
    stickyBar.toggleAttribute('inert', !shown);
    stickyBar.setAttribute('aria-hidden', shown ? 'false' : 'true');

    // Une fois affichée, la réserve de bas de page reste en place : pas de saut au pied de page.
    if (shown) document.documentElement.setAttribute('data-pjc-sticky-pad', '');
    this.#publishHeight();
  }

  /* --- Contenu (état d'achat partagé) ------------------------------------------------------------- */

  /** @param {CustomEvent} event */
  #onPurchase = (event) => {
    this.#render(event.detail);
  };

  /**
   * @param {any} purchase - window.PJC_PURCHASE enrichi par pjc-buy.js (priceText, compareText, cta).
   */
  #render(purchase) {
    if (!purchase || typeof purchase !== 'object') return;
    const { stickyBar, addToCartButton, price, priceFrom, priceValue, priceCompare, compareValue, label } = this.refs;
    const pack = purchase.mode !== 'single';

    stickyBar.dataset.mode = pack ? 'pack' : 'single';
    stickyBar.dataset.from = purchase.fromPrice ? 'true' : 'false';

    if (priceValue && purchase.priceText) priceValue.textContent = purchase.priceText;
    if (price) price.hidden = !purchase.priceText;
    if (priceFrom) priceFrom.hidden = !purchase.fromPrice;
    if (priceCompare) {
      priceCompare.hidden = !purchase.compareText;
      if (compareValue && purchase.compareText) compareValue.textContent = purchase.compareText;
    }

    const wantedLabel = purchase.cta?.sticky;
    if (label && wantedLabel && label.textContent !== wantedLabel) label.textContent = wantedLabel;

    // Sélection indisponible : bouton désactivé. Choix manquant : bouton actif (il ramène au choix).
    const unavailable = !purchase.ready && (purchase.missing?.length ?? 0) === 0;
    if (addToCartButton.disabled !== unavailable) addToCartButton.disabled = unavailable;

    // Pas d'éclat de fête (pjc-fx.js) tant que le clic n'ajoutera rien.
    if (addToCartButton.hasAttribute('data-pjc-no-sparkle') === Boolean(purchase.ready)) {
      addToCartButton.toggleAttribute('data-pjc-no-sparkle', !purchase.ready);
    }
  }

  /** @param {CustomEvent} event */
  #onBuyState = (event) => {
    const detail = event.detail;
    if (!detail?.broadcast) return;

    switch (detail.state) {
      case 'loading':
        this.#setPhase('loading');
        break;
      case 'success':
        this.#setPhase('success');
        if (detail.source === 'sticky') this.#flyToCart();
        break;
      case 'idle':
        this.#setPhase('idle');
        break;
      case 'error':
      case 'notice':
        this.#setPhase('idle');
        this.#showMessage(detail.message, detail.state);
        break;
      case 'clear':
        this.#hideMessage();
        break;
    }
  };

  /** @param {'idle' | 'loading' | 'success'} phase */
  #setPhase(phase) {
    const { stickyBar, addToCartButton } = this.refs;
    stickyBar.dataset.pjcState = phase;
    if (phase === 'loading') {
      addToCartButton.setAttribute('aria-busy', 'true');
      this.#hideMessage();
    } else {
      addToCartButton.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} text
   * @param {'error' | 'notice'} kind
   */
  #showMessage(text, kind) {
    const { message } = this.refs;
    if (!message) return;
    message.textContent = text;
    message.dataset.kind = kind;
    message.hidden = false;
  }

  #hideMessage() {
    const { message } = this.refs;
    if (message) message.hidden = true;
  }

  /** @param {any} event */
  #onProductSelect = (event) => {
    event.promise
      ?.then((/** @type {any} */ result) => {
        const detail = result?.detail;
        const source = detail?.resource?.featured_image?.src;
        const { productImage } = this.refs;
        if (!source || !productImage || (detail.productId && detail.productId !== this.dataset.productId)) return;

        const url = new URL(source, window.location.href);
        url.searchParams.set('width', '96');
        productImage.src = url.toString();
      })
      .catch(() => {});
  };

  /* --- Actions ---------------------------------------------------------------------------------- */

  /**
   * Le bouton de la barre émet pjc:purchase-submit ; pjc-buy.js prend la main (choix manquant, pack,
   * ou ajout via le bouton principal). Sans pjc-buy.js, repli : clic sur le bouton principal.
   */
  handleAddToCartClick = () => {
    const detail = { source: 'sticky', handled: false };
    document.dispatchEvent(new CustomEvent('pjc:purchase-submit', { cancelable: true, detail }));
    if (detail.handled) return;

    const mainButton = /** @type {HTMLElement | null | undefined} */ (
      this.#block?.querySelector('[data-pjc-buy-button="main"]')
    );
    mainButton?.click();
  };

  /** Vol de la vignette vers le panier, joué uniquement après un ajout réussi depuis la barre. */
  #flyToCart() {
    const { productImage } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!productImage || !cartIcon || this.dataset.fly !== 'true') return;
    if (window.matchMedia(REDUCED_MOTION_QUERY).matches || !customElements.get('fly-to-cart')) return;

    const flyToCart = /** @type {HTMLElement & {source: Element, destination: Element, useSourceSize: string}} */ (
      document.createElement('fly-to-cart')
    );
    flyToCart.classList.add('fly-to-cart--sticky');
    flyToCart.style.setProperty('background-image', `url(${productImage.currentSrc || productImage.src})`);
    flyToCart.useSourceSize = 'true';
    flyToCart.source = productImage;
    flyToCart.destination = cartIcon;
    document.body.appendChild(flyToCart);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
